import { useEffect, useState } from "react";
import CorreosParaIA from "@/components/correo/CorreosParaIA";
import { ApiError, api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { ayudaResponderProveedor } from "@/components/ayuda/vistas/responderProveedor";
import AyudaVista from "@/ui/AyudaVista";
import Modal from "@/ui/Modal";
import { useToast } from "@/ui/Toast";

/** ¿Hay lugar para la columna de referencia al lado? Se mide una vez, al abrir. */
const hayEspacio = () =>
  typeof window !== "undefined" && window.matchMedia("(min-width: 1024px)").matches;

type NotaContexto = {
  fecha: string;
  autor: string | null;
  texto: string;
  tags: string[];
};

type Contexto = {
  bitacora: NotaContexto[];
  evidencia_url: string | null;
  puede_generar_evidencia: boolean;
};

type Props = {
  /** Caso al que pertenece el ticket; de ahí salen los apuntes y evidencias. */
  casoId: number;
  /** Cómo se llama el proveedor en los textos: "Jimi", "Queclink". */
  proveedor: string;
  /** Identificador visible del ticket, para que se vea a cuál se responde. */
  ticket: string;
  /** Ruta del endpoint que envía (cada proveedor tiene la suya). */
  rutaEnviar: string;
  /**
   * Ruta que traduce al inglés. Si no viene, no se ofrece traducir — Queclink
   * atiende en español y no tiene caso gastar IA ahí.
   */
  rutaTraducir?: string;
  /**
   * Ruta que abre un ticket de SEGUIMIENTO cuando el proveedor cerró el suyo
   * (solo Queclink: su Zendesk no deja reabrir un cerrado). Con ella, si el
   * envío choca con un ticket cerrado (409), la ventana cambia a modo
   * seguimiento sin perder lo escrito.
   */
  rutaSeguimiento?: string;
  /** El ticket ya se sabe cerrado: la ventana abre directo en modo seguimiento. */
  cerrado?: boolean;
  /** Se descubrió cerrado al enviar: el caso debe refrescarse para mostrarlo. */
  onTicketCerrado?: () => void;
  onClose: () => void;
  onEnviado: () => void;
};

/**
 * Contesta en el ticket de un proveedor (Jimi, Queclink) sin salir del caso.
 *
 * Tres cosas mandan en el diseño de esta ventana:
 *
 * 1. **No se puede deshacer.** Jimi no deja borrar ni editar una respuesta ya
 *    puesta, así que el envío pide confirmación explícita — a diferencia del
 *    resto de la app, donde casi todo se corrige.
 * 2. **Pasar a inglés es opcional.** El técnico de Jimi lee bien en español
 *    (Joe lo confirmó el 2026-10-13; un comentario viejo aquí decía lo
 *    contrario). El botón queda a la mano para quien prefiera mandarlo en
 *    inglés, y la traducción se revisa ANTES de mandar (mismo criterio que el
 *    botón de IA de los correos: se ve, luego se manda).
 * 3. **Lo que hace falta para redactar se trae aquí**: los apuntes del caso y
 *    el link del ZIP de evidencias. Sin eso había que cerrar la ventana e irse
 *    a otra pestaña a recordar qué se probó — y al volver, el texto se perdía.
 *
 * Modo **seguimiento** (solo Queclink, 2026-10-13): si el proveedor CERRÓ el
 * ticket, la misma ventana abre uno nuevo ligado al cerrado. Llega ahí de dos
 * formas: el caso ya lo sabía cerrado (`cerrado`), o el envío chocó con un 409
 * — y entonces cambia de modo sin borrar lo que se escribió.
 */
export default function ResponderProveedorModal({
  casoId,
  proveedor,
  ticket,
  rutaEnviar,
  rutaTraducir,
  rutaSeguimiento,
  cerrado,
  onTicketCerrado,
  onClose,
  onEnviado,
}: Props) {
  const toast = useToast();
  // La IA cuesta tokens: se gatea por usuario con el mismo flag del asistente
  // de correos. Se lee de la sesión y no del contexto para que el botón no
  // aparezca a destiempo cuando la carga tarda.
  const iaHabilitada = useAuth((s) => s.user?.ai_comentarios_enabled);
  const [ctx, setCtx] = useState<Contexto | null>(null);
  const [evidenciaUrl, setEvidenciaUrl] = useState<string | null>(null);
  const [texto, setTexto] = useState("");
  const [traduciendo, setTraduciendo] = useState(false);
  const [redactando, setRedactando] = useState(false);
  const [generando, setGenerando] = useState(false);
  const [enviando, setEnviando] = useState(false);
  // En escritorio la referencia vive en su propia columna y ahí estorba menos
  // abierta que cerrada: se ve de un vistazo mientras escribes. En móvil va
  // plegada, que es donde cada renglón cuesta pantalla.
  const [verApuntes, setVerApuntes] = useState(hayEspacio);
  const [confirmando, setConfirmando] = useState(false);
  // Modo seguimiento: el proveedor cerró el ticket y lo que se escribe abre
  // uno NUEVO ligado al anterior, en vez de contestar en el cerrado.
  const [seguimiento, setSeguimiento] = useState(Boolean(cerrado && rutaSeguimiento));
  // Correos del caso que la IA va a leer. Arrancan en cero por decisión de Joe:
  // él prende los que sirven (ver `components/correo/CorreosParaIA`).
  const [correosIA, setCorreosIA] = useState<number[]>([]);

  const vacio = texto.trim() === "";
  const ocupado = enviando || traduciendo || redactando;

  useEffect(() => {
    let vivo = true;
    void (async () => {
      try {
        const r = await api.get<Contexto>(`/api/casos/${casoId}/contexto-proveedor`);
        if (!vivo) return;
        setCtx(r);
        setEvidenciaUrl(r.evidencia_url);
      } catch {
        // El contexto es una ayuda, no un requisito: si falla, la ventana
        // sigue sirviendo para escribir y mandar.
      }
    })();
    return () => {
      vivo = false;
    };
  }, [casoId]);

  function agregarAlFinal(linea: string) {
    setTexto((prev) => (prev.trim() ? `${prev.trim()}\n\n${linea}` : linea));
    setConfirmando(false);
  }

  async function traducir() {
    if (vacio || ocupado) return;
    setTraduciendo(true);
    try {
      const r = await api.post<{ texto: string }>(rutaTraducir!, { texto });
      setTexto(r.texto);
      toast.success("Listo, ya está en inglés", "Revísalo antes de mandarlo");
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No pude traducirlo", msg);
    } finally {
      setTraduciendo(false);
    }
  }

  async function redactarConIA() {
    if (ocupado) return;
    setRedactando(true);
    try {
      // Mismo asistente que redacta los correos, en su modo "proveedor": toma
      // lo que ya está escrito como guía y lo arma con el contexto del caso.
      // `en_hilo` es lo que lo hace RESPONDER en vez de abrir: sin eso redactaba
      // «nos ponemos en contacto para solicitar apoyo» dentro de un ticket que
      // ya llevaba días, y sin ver lo que el proveedor había escrito.
      const r = await api.post<{ texto: string }>(
        `/api/casos/${casoId}/correos/generar-comentario`,
        { destinatario: "jimi", guia: texto, en_hilo: true, correos_ids: correosIA },
      );
      setTexto(r.texto);
      setConfirmando(false);
      toast.success("Listo, ya lo redactó la IA", "Revísalo antes de mandarlo");
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No pude redactarlo", msg);
    } finally {
      setRedactando(false);
    }
  }

  async function generarEvidencia() {
    if (generando || ocupado) return;
    setGenerando(true);
    try {
      // El endpoint vive bajo /jimi/ por historia, pero arma el ZIP igual para
      // cualquier proveedor: sin contraseña, para que no batallen del otro lado.
      const r = await api.post<{ evidencia_url: string }>(
        `/api/casos/${casoId}/jimi/evidencia`,
      );
      setEvidenciaUrl(r.evidencia_url);
      toast.success("Paquete de evidencias listo", "Ya puedes agregarlo a la respuesta");
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No pude generar el paquete", msg);
    } finally {
      setGenerando(false);
    }
  }

  async function enviar() {
    if (vacio || enviando) return;
    setEnviando(true);
    try {
      if (seguimiento && rutaSeguimiento) {
        const r = await api.post<{ ticket_id: number }>(rutaSeguimiento, { texto });
        toast.success(
          `Ticket de seguimiento abierto en ${proveedor}`,
          `#${r.ticket_id} (continúa el ${ticket})`,
        );
      } else {
        await api.post(rutaEnviar, { texto });
        toast.success(`Respuesta enviada a ${proveedor}`, ticket);
      }
      onEnviado();
      onClose();
    } catch (err) {
      // El ticket se cerró mientras se escribía (o el dato guardado era de
      // hace una hora): el servidor NO mandó nada. Se cambia a seguimiento y
      // el texto se queda donde está.
      if (!seguimiento && rutaSeguimiento && err instanceof ApiError && err.status === 409) {
        setSeguimiento(true);
        setConfirmando(false);
        toast.warning(
          `${proveedor} ya cerró el ticket ${ticket}`,
          "No se mandó nada. Tu texto sigue aquí: ahora se abre como ticket de seguimiento.",
        );
        onTicketCerrado?.();
        return;
      }
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error(
        seguimiento ? "No pude abrir el ticket de seguimiento" : "No pude enviar la respuesta",
        msg,
      );
      setConfirmando(false);
    } finally {
      setEnviando(false);
    }
  }

  const titulo = seguimiento ? `Ticket de seguimiento en ${proveedor}` : `Responderle a ${proveedor}`;

  return (
    <Modal
      open
      onOpenChange={(o) => {
        if (!o && !enviando) onClose();
      }}
      title={titulo}
      description={
        seguimiento ? `Continúa el ticket ${ticket}, que ${proveedor} cerró` : `Ticket ${ticket}`
      }
      size="lg"
      ayuda={
        <AyudaVista
          titulo={titulo}
          {...ayudaResponderProveedor({
            proveedor,
            ticket,
            seguimiento,
            puedeSeguimiento: Boolean(rutaSeguimiento),
            iaHabilitada: Boolean(iaHabilitada),
            puedeTraducir: Boolean(iaHabilitada && rutaTraducir),
          })}
        />
      }
      footer={
        <>
          <button
            type="button"
            className="btn-ghost text-sm"
            onClick={onClose}
            disabled={enviando}
          >
            Cancelar
          </button>
          {confirmando ? (
            <button
              type="button"
              className="btn bg-amber-600 hover:bg-amber-500 text-white text-sm"
              onClick={() => void enviar()}
              disabled={enviando}
            >
              {enviando
                ? seguimiento
                  ? "Abriendo…"
                  : "Enviando…"
                : seguimiento
                  ? "Sí, abrir el ticket de seguimiento"
                  : `Sí, mandarlo a ${proveedor}`}
            </button>
          ) : (
            <button
              type="button"
              className="btn-primary text-sm"
              onClick={() => setConfirmando(true)}
              disabled={vacio || ocupado}
            >
              {seguimiento ? "Abrir ticket de seguimiento" : "Enviar"}
            </button>
          )}
        </>
      }
    >
      {/* Dos columnas en escritorio: se redacta a la izquierda y se consulta a
          la derecha, sin plegar y desplegar cada vez. En móvil, apilado — ahí
          la referencia va plegada porque cada renglón cuesta pantalla. */}
      <div className="space-y-3 lg:grid lg:min-h-0 lg:flex-1 lg:grid-cols-[minmax(0,1.45fr)_minmax(0,1fr)] lg:gap-5 lg:space-y-0">
        <div className="space-y-3 lg:flex lg:min-h-0 lg:flex-col lg:space-y-0 lg:gap-3">
          {seguimiento ? (
            <div className="space-y-1 text-xs text-amber-300/90 bg-amber-500/10 border border-amber-500/30 rounded-md px-3 py-2">
              <p>
                <strong>{proveedor} cerró el ticket {ticket}</strong> y ya no acepta
                respuestas. Esto abre un <strong>ticket nuevo</strong> en su sistema,
                ligado al anterior y con el mismo asunto (lleva el TE del caso).{" "}
                <strong>No se puede borrar ni editar</strong> después.
              </p>
              <p className="text-amber-200/70">
                Escríbelo para que se entienda solo: quien lo tome tal vez no lea el
                ticket anterior.
              </p>
            </div>
          ) : (
            <p className="text-xs text-amber-300/90 bg-amber-500/10 border border-amber-500/30 rounded-md px-3 py-2">
              Esto se escribe <strong>en el sistema de {proveedor}</strong> y{" "}
              <strong>no se puede borrar ni editar</strong> después. Léelo bien antes
              de mandarlo.
            </p>
          )}

          {/* El compositor: `flex-1` SIN piso deja que el textarea ceda todo su
              alto, y con poco material a la derecha el modal se encoge y acabas
              escribiendo por una rendija con barra propia (lo reportó Joe en el
              de Queclink, que tiene un solo apunte). El piso manda cuando sobra
              sitio; el `flex-1`, cuando falta. */}
          <textarea
            value={texto}
            onChange={(e) => {
              setTexto(e.target.value);
              setConfirmando(false);
            }}
            rows={9}
            autoFocus
            disabled={enviando}
            placeholder={
              seguimiento
                ? `Cuéntale a ${proveedor} qué sigue pendiente y qué necesitas…`
                : `Escribe la respuesta para el técnico de ${proveedor}…`
            }
            className="input w-full font-normal lg:min-h-[20rem] lg:flex-1"
            aria-label={
              seguimiento
                ? `Mensaje del ticket de seguimiento para ${proveedor}`
                : `Respuesta para ${proveedor}`
            }
          />

          <div className="flex flex-wrap items-center gap-2">
            {iaHabilitada && (
              <button
                type="button"
                onClick={() => void redactarConIA()}
                disabled={ocupado}
                className="btn-ghost text-xs disabled:opacity-50"
              >
                {redactando ? "Redactando…" : "✨ Redactar con IA"}
              </button>
            )}

            {iaHabilitada && rutaTraducir && (
              <button
                type="button"
                onClick={() => void traducir()}
                disabled={vacio || ocupado}
                className="btn-ghost text-xs disabled:opacity-50"
              >
                {traduciendo ? "Traduciendo…" : "🌐 Pasarlo a inglés"}
              </button>
            )}

            {evidenciaUrl ? (
              <button
                type="button"
                onClick={() => agregarAlFinal(`Evidencias del equipo: ${evidenciaUrl}`)}
                disabled={enviando}
                className="btn-ghost text-xs disabled:opacity-50"
              >
                📎 Agregar link de evidencias
              </button>
            ) : (
              ctx?.puede_generar_evidencia && (
                <button
                  type="button"
                  onClick={() => void generarEvidencia()}
                  disabled={generando || enviando}
                  className="btn-ghost text-xs disabled:opacity-50"
                >
                  {generando ? "Generando…" : "📦 Generar paquete de evidencias"}
                </button>
              )
            )}
          </div>

          {confirmando && (
            <p className="text-xs text-amber-300">
              {seguimiento
                ? "Se va a abrir un ticket nuevo con este texto, tal cual. ¿Le damos?"
                : "Se va a mandar tal cual está arriba. ¿Le damos?"}
            </p>
          )}
        </div>

        {/* Material de consulta: los correos que puede leer la IA y los apuntes
            del caso. Sin esto había que cerrar la ventana e irse a otra pestaña
            a recordar qué se probó — y al volver, el texto se perdía. */}
        <div className="space-y-3 border-t border-ink-700 pt-3 lg:min-h-0 lg:overflow-y-auto lg:border-l lg:border-t-0 lg:pl-5 lg:pt-0 scroll-sutil">
          {iaHabilitada && (
            <CorreosParaIA
              casoId={casoId}
              seleccion={correosIA}
              onCambio={setCorreosIA}
              abiertaInicial={hayEspacio()}
            />
          )}

          {(ctx?.bitacora.length ?? 0) > 0 && (
            <div>
              <button
                type="button"
                onClick={() => setVerApuntes((v) => !v)}
                className="text-xs text-zinc-400 hover:text-zinc-200 transition"
              >
                {verApuntes ? "▾" : "▸"} Apuntes del caso ({ctx?.bitacora.length})
              </button>
              {verApuntes && (
                <ul className="mt-2 space-y-2 max-h-52 overflow-y-auto lg:max-h-none scroll-sutil">
                  {ctx?.bitacora.map((n, i) => (
                    <li
                      key={i}
                      className="rounded border border-ink-700 bg-ink-800/40 px-2 py-1.5"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-[10px] text-zinc-500">
                          {n.fecha}
                          {n.autor ? ` · ${n.autor}` : ""}
                        </span>
                        <button
                          type="button"
                          onClick={() => agregarAlFinal(n.texto)}
                          disabled={enviando}
                          className="text-[10px] text-accent-400 hover:text-accent-300 disabled:opacity-50"
                        >
                          + agregar
                        </button>
                      </div>
                      <p className="text-xs text-zinc-300 whitespace-pre-wrap">{n.texto}</p>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}
